import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, BookOpen, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JourneyPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [userSessionId] = useState(() => {
    return localStorage.getItem('user_session_id') || '';
  });

  const moods = [
    { value: 'tired', label: 'Tired', emoji: '🌙' },
    { value: 'heavy', label: 'Heavy', emoji: '🌧️' },
    { value: 'healing', label: 'Healing', emoji: '🌿' },
    { value: 'calm', label: 'Calm', emoji: '☀️' },
    { value: 'determined', label: 'Determined', emoji: '🔥' }
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/diary/entries/${userSessionId}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load journey');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (entryId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodEmoji = (moodValue) => {
    const mood = moods.find(m => m.value === moodValue);
    return mood ? mood.emoji : '';
  };

  const getMoodLabel = (moodValue) => {
    const mood = moods.find(m => m.value === moodValue);
    return mood ? mood.label : '';
  };

  // Group entries by month
  const groupedEntries = entries.reduce((groups, entry) => {
    const date = new Date(entry.created_at);
    const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(entry);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#C8B5E0] mx-auto mb-4 animate-pulse" />
          <p className="text-[#EDE8F3] text-lg">Loading your journey...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#E7C6C2]" />
              <h1 className="text-2xl font-bold text-[#EDE8F3]">My Journey</h1>
            </div>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {entries.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-[#C8B5E0] mx-auto mb-4 opacity-50" />
            <p className="text-[#EDE8F3] text-xl mb-2">Your journey starts with a single word</p>
            <p className="text-[#C8B5E0] mb-6 max-w-md mx-auto">
              Start writing in your diary to see your journey unfold here.
            </p>
            <Button
              onClick={() => navigate('/inner-voice')}
              className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Write Your First Entry
            </Button>
          </div>
        ) : (
          <div>
            {/* Summary */}
            <div className="bg-gradient-to-br from-[#3E324D] to-[#2E2536] rounded-2xl p-6 mb-8 border-2 border-[#E7C6C2]/20">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-[#E7C6C2]" />
                <h2 className="text-xl font-semibold text-[#EDE8F3]">Your Healing Story</h2>
              </div>
              <p className="text-[#C8B5E0] mb-4">
                You've written <span className="text-[#EDE8F3] font-semibold text-lg">{entries.length}</span> {entries.length === 1 ? 'entry' : 'entries'} 
              </p>
              
              {/* Mood breakdown if available */}
              {entries.some(e => e.mood) && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {moods.map(mood => {
                    const count = entries.filter(e => e.mood === mood.value).length;
                    if (count === 0) return null;
                    return (
                      <div 
                        key={mood.value}
                        className="bg-[#2E2536] px-3 py-2 rounded-lg flex items-center gap-2"
                      >
                        <span className="text-xl">{mood.emoji}</span>
                        <span className="text-sm text-[#EDE8F3]">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Timeline by Month */}
            {Object.entries(groupedEntries).map(([monthYear, monthEntries]) => (
              <div key={monthYear} className="mb-8">
                <h3 className="text-lg font-semibold text-[#EDE8F3] mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#E7C6C2]" />
                  {monthYear}
                </h3>
                
                <div className="space-y-4">
                  {monthEntries.map((entry) => {
                    const isExpanded = expandedEntries.has(entry.id);
                    const previewText = entry.text_entry.length > 150 
                      ? entry.text_entry.substring(0, 150) + '...'
                      : entry.text_entry;

                    return (
                      <div 
                        key={entry.id}
                        className="bg-[#3E324D] rounded-xl p-6 hover:bg-[#4A3B5A] transition-colors"
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {entry.mood && (
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{getMoodEmoji(entry.mood)}</span>
                                <span className="text-sm text-[#C8B5E0]">{getMoodLabel(entry.mood)}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-[#C8B5E0]">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>

                        {/* Entry Text */}
                        <p className="text-[#EDE8F3] leading-relaxed whitespace-pre-wrap mb-4">
                          {isExpanded ? entry.text_entry : previewText}
                        </p>

                        {/* Expand/Collapse Button */}
                        {entry.text_entry.length > 150 && (
                          <button
                            onClick={() => toggleExpand(entry.id)}
                            className="text-[#E7C6C2] hover:text-[#F7E5E5] text-sm flex items-center gap-1 mb-4"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                Show less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                Read more
                              </>
                            )}
                          </button>
                        )}

                        {/* Auto-Reply */}
                        {entry.auto_reply_used && (
                          <div className="bg-[#2E2536] rounded-lg p-4 border-l-4 border-[#E7C6C2]">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-4 h-4 text-[#E7C6C2]" />
                              <span className="text-sm text-[#E7C6C2] font-medium">Whisper from SheSafe Haven</span>
                            </div>
                            <p className="text-[#C8B5E0] text-sm italic">
                              "{entry.auto_reply_used}"
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Footer Tip */}
            <div className="mt-8 text-center">
              <p className="text-sm text-[#C8B5E0] italic max-w-md mx-auto">
                Some days, revisiting an old entry brings new meaning. Your journey is proof of your growth.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneyPage;
